import React from 'react';
import { NavLink } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import '../Stlye.css';
import ServiceFaqSection from '../Components/ServiceFaqSection';
import MetaTags from '../MetaTags';

import BannerImage from '../images/AmlyticsAI/amlytics_top_banner_1920x1080.webp';
import Section1Image from '../images/AmlyticsAI/The Challenge1200X900.webp';
import Section2Image from '../images/AmlyticsAI/The Solution What We Built 1200X900.webp';
import Section3Image from '../images/AmlyticsAI/Built for Businesses 1200X900.webp';
import FaqImage from '../images/AmlyticsAI/Frequently Asked Questions_1200x1800.webp';

const AmlyticsAI = () => {
  const faqData = [
    {
      question: 'What are the Tranche 2 AML/CTF reforms, and who do they affect?',
      answer: '"Tranche 2" isn\'t a separate government stage or rollout phase — it\'s the industry name for the Anti-Money Laundering and Counter-Terrorism Financing Amendment Act 2024, a reform that amends Australia\'s existing AML/CTF Act 2006. That original 2006 Act already covered banks, casinos, and remittance and digital currency services — informally "Tranche 1" entities. The 2024 amendment extends the same Act\'s obligations to "gatekeeper" professions: lawyers, conveyancers, accountants, real estate professionals and property developers, trust and company service providers, and dealers in precious metals and stones. It\'s the activity, not the job title, that triggers obligations — a firm is captured when it provides a "designated service" under the amended Act, such as managing client money or helping restructure a company.',
    },
    {
      question: 'When did Tranche 2 come into effect, and what was the enrolment deadline?',
      answer: 'AUSTRAC enrolment for newly regulated entities opened on 31 March 2026. Obligations commenced on 1 July 2026, and businesses providing a designated service were required to enrol within 28 days of starting — by 29 July 2026 at the latest. Those dates have now passed, so any business that provides a designated service and hasn\'t enrolled yet is already overdue and should do so as soon as possible.',
    },
    {
      question: 'How do I know if my business is an AUSTRAC reporting entity?',
      answer: 'Map your services against AUSTRAC\'s list of "designated services." If any part of what you do falls on that list — even occasionally — you\'re a reporting entity for that activity, regardless of your profession or business size.',
    },
    {
      question: 'What happens if a business doesn\'t comply?',
      answer: 'AUSTRAC can seek civil penalties of up to A$31.3 million per contravention for a corporation, with penalties also available against individual directors and officers. Failing to enrol, failing to file a required report, or "tipping off" a customer are each treated as serious breaches.',
    },
    {
      question: 'How much does AML/CTF compliance actually cost a small business?',
      answer: 'Cost scales with turnover. Government impact analysis estimates put upfront compliance costs at roughly $4,000 for businesses under $200,000 turnover, rising to around $28,000+ for larger businesses, with ongoing annual costs on top. Software that automates screening and reporting is generally far cheaper than running the same checks manually or hiring extra compliance staff.',
    },
    {
      question: 'What\'s the difference between AML software and a full AML/CTF program?',
      answer: 'They\'re not the same thing. Your AML/CTF program is the documented, board-approved risk assessment and set of policies AUSTRAC requires (Part A and Part B). AML software — like AMLyticsAI — is the operational layer that executes and evidences that program: running KYC, screening, transaction monitoring, and generating the reports and audit trail your program commits you to.',
    },
    {
      question: 'Do sole traders and small firms really need dedicated AML software?',
      answer: 'Yes. Many newly regulated Tranche 2 businesses are small practices meeting these obligations for the first time, and manual spreadsheet-based compliance is slow and error-prone at any size. Pay-as-you-go and low-cost monthly options exist specifically so a sole practitioner isn\'t priced out of proper compliance tooling.',
    },
    {
      question: 'Do I need to check customers I already had before Tranche 2 started?',
      answer: 'Yes — pre-commencement (existing) customers should be brought into your ongoing monitoring, with priority given to higher-risk relationships and to anyone whose details are out of date or whose relationship with you materially changes (e.g. a contract renewal).',
    },
    {
      question: 'What\'s the difference between a Suspicious Matter Report (SMR) and a Threshold Transaction Report (TTR)?',
      answer: 'An SMR is filed when you form a reasonable suspicion that a customer or transaction is linked to money laundering or another offence. A TTR is filed for cash transactions of A$10,000 or more. They serve different triggers and both need to be filed correctly to stay compliant.',
    },
    {
      question: 'How long do I have to keep AML/CTF records?',
      answer: 'Seven years, covering customer identification, transaction records, screening results and reports — all produced to AUSTRAC on request.',
    },
    {
      question: 'Is AMLyticsAI built specifically for AUSTRAC, or is it a global platform adapted for Australia?',
      answer: 'Built specifically for AUSTRAC. Several AML tools available to Australian businesses are actually priced and built for other markets (New Zealand or global standards) and adapted afterward. AMLyticsAI was designed around the AML/CTF Act 2006 and AML/CTF Rules 2025 from the outset, by a team that also built it, so workflows map directly to what AUSTRAC expects rather than being retrofitted.',
    },
    {
      question: 'Can I pass AML compliance costs on to my clients?',
      answer: 'Many businesses do fold some or all of their AML compliance costs into onboarding fees or a dedicated AML fee — this is common practice, though how you structure it is a commercial decision for your business.',
    },
  ];

  return (
    <div className="management-consulting amlytics-ai-product">
      <MetaTags
        title="AMLyticsAI: AUSTRAC AML Software Built by Smart Resources"
        description="Smart Resources QLD built AMLyticsAI, an AI-powered AML/CTF compliance platform helping AUSTRAC reporting entities across Australia meet Tranche 2."
        keywords="AMLyticsAI, AUSTRAC AML software, AML CTF compliance, Tranche 2 AML Australia, transaction monitoring, KYC software, SMR TTR reporting"
        Canonical="https://smartresourcesqld.com.au/products/amlyticsai"
        ogImage={BannerImage}
        ogTitle="AMLyticsAI: AUSTRAC AML Software Built by Smart Resources"
        ogDescription="Smart Resources QLD built AMLyticsAI, an AI-powered AML/CTF compliance platform helping AUSTRAC reporting entities across Australia meet Tranche 2."
        ogUrl="https://smartresourcesqld.com.au/products/amlyticsai"
        DCTitle="AMLyticsAI: AUSTRAC AML Software Built by Smart Resources"
        DCDescription="Smart Resources QLD built AMLyticsAI, an AI-powered AML/CTF compliance platform helping AUSTRAC reporting entities across Australia meet Tranche 2."
        DCSubject="AUSTRAC AML Software Built by Smart Resources QLD"
      />

      <section className="innr_banner" style={{ backgroundImage: `url("${BannerImage}")`, backgroundPosition: 'center', backgroundSize: 'cover', backgroundRepeat: 'no-repeat' }}>
        <div className="page_title_innr" data-aos="fade-down">
          <div className="page_title">AMLyticsAI</div>
          <ul className="bread_crumb">
            <li><NavLink to="/">Home</NavLink></li>
            <li><NavLink to="/products/amlyticsai">Product</NavLink></li>
            <li>AMLyticsAI</li>
          </ul>
        </div>
      </section>

      <section className="pricing servinnrbg">
        <div className="servinnng_middle">
          <div className="container">
            <div className="row">
              <div className="col-lg-12 col-md-12 col-sm-12">
                <div className="global_left serv_innr_heading" data-aos="fade-up">
                  <h1>Case Study: Building AMLyticsAI — AUSTRAC-Ready AML/CTF Compliance Software</h1>
                  <p style={{ fontSize: '16px' }}>
                    Smart Resources QLD doesn't just advise on data and technology strategy, we build the software that puts it into practice. AMLyticsAI is a live example: <a href="https://amlyticsai.com/" target="_blank" rel="noopener noreferrer" style={{ color: '#0d6efd', textDecoration: 'underline' }}>an AI-powered AML/CTF compliance platform</a> we developed in-house for AUSTRAC reporting entities across Australia, from Brisbane and Sydney to Melbourne, Perth, Adelaide and beyond.
                  </p>
                  <p style={{ fontSize: '16px' }}>
                    This case study walks through the problem we set out to solve, how we approached the build, and what the platform does for the businesses now relying on it.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="discuss-seo seo_price_bg management_inner_bg">
          <div className="container">
            <div className="row discuss-ppc-seo">
              <h2>Talk to our team about your project</h2>
              <div className="button-ppc-new">
                <NavLink
                  className="btn-trans"
                  to="/contact-us"
                  onClick={() => window.scrollTo(0, 0)}
                >
                  Contact Smart Resources QLD
                </NavLink>
              </div>
            </div>
          </div>
        </div>

        <section className="include_services">
          {/* Section 1: The Challenge (Image Left, Content Right) */}
          <div className="include_service_innr">
            <Container>
              <Row className="align-items-stretch">
                <Col lg="4" md="6" sm="12" className="d-flex mb-3 mb-lg-0">
                  <div className="include_img w-100 d-flex" data-aos="fade-right" style={{ marginLeft: 0 }}>
                    <img src={Section1Image} alt="The Challenge - AML/CTF Regulatory Compliance" className="img-fluid w-100" style={{ borderRadius: '20px', objectFit: 'cover', height: '100%', minHeight: '320px' }} />
                  </div>
                </Col>

                <Col lg="8" md="6" sm="12" className="d-flex align-items-center">
                  <div className="include_text w-100" data-aos="fade-left">
                    <h2>The Challenge: A Regulatory Deadline With No Off-the-Shelf Fix</h2>
                    <p style={{ fontSize: '16px', lineHeight: '26px' }}>
                      <a href="https://amlyticsai.com/" target="_blank" rel="noopener noreferrer" style={{ color: '#0d6efd', textDecoration: 'underline' }}>Australia's AML/CTF regime is expanding.</a> Under the Tranche 2 reforms, obligations that once applied mainly to banks and financial institutions now extend to "gatekeeper" professions — lawyers, accountants, conveyancers, real estate agents, property developers, and trust and company service providers. AUSTRAC estimates the change brings tens of thousands of newly regulated businesses into scope, many of which have never had to enrol, screen customers, monitor transactions, or file a Suspicious Matter Report before.
                    </p>
                    <p style={{ fontSize: '16px', lineHeight: '26px' }}>
                      Generic, offshore AML tools weren't built around AUSTRAC's specific rules, reporting formats, or the compressed timeline firms were facing. That gap was the brief: build compliance software shaped around Australian obligations from day one, not a global platform retrofitted to fit them.
                    </p>
                  </div>
                </Col>
              </Row>
            </Container>
          </div>

          {/* Section 2: The Solution (Content Left, Image Right) */}
          <div className="include_service_innr">
            <Container>
              <Row className="align-items-stretch">
                <Col lg="8" md="6" sm="12" className="include_textpartts d-flex align-items-center mb-3 mb-lg-0">
                  <div className="include_text w-100" data-aos="fade-right">
                    <h2>The Solution: What We Built</h2>
                    <p style={{ fontSize: '16px', lineHeight: '26px' }}>
                      Our team combined data science, software engineering, and regulatory research to design AMLyticsAI as a single platform covering the full compliance workflow, rather than a patchwork of point tools:
                    </p>
                    <div className="pf">
                      <ul>
                        <li>
                          <strong>KYC &amp; Customer Due Diligence</strong> — automated identity verification, beneficial ownership checks, and risk scoring
                        </li>
                        <li>
                          <strong>Transaction Monitoring</strong> — real-time detection of structuring, layering, and rapid-movement typologies
                        </li>
                        <li>
                          <strong>Sanctions &amp; PEP Screening</strong> — continuous screening against UN, OFAC, DFAT, EU and UK lists, plus adverse media
                        </li>
                        <li>
                          <strong>Risk Dashboards</strong> — a live view of alert trends, watchlist hits, and overall ML/TF risk exposure
                        </li>
                        <li>
                          <strong>Regulatory Reporting</strong> — <a href="https://amlyticsai.com/blog/smrs-vs-ttrs" target="_blank" rel="noopener noreferrer" style={{ color: '#0d6efd', textDecoration: 'underline' }}>SMR and TTR generation formatted for AUSTRAC submission</a>, with seven-year record-keeping built in
                        </li>
                      </ul>
                    </div>
                    <p className="mt-3" style={{ fontSize: '16px', lineHeight: '26px' }}>
                      Every workflow was mapped directly to the AML/CTF Act 2006 and the AML/CTF Rules 2025, including the onboarding steps newly regulated <a href="https://amlyticsai.com/blog/tranche-2-explained" target="_blank" rel="noopener noreferrer" style={{ color: '#0d6efd', textDecoration: 'underline' }}>Tranche 2</a> entities need to meet their 29 July 2026 enrolment deadline.
                    </p>
                  </div>
                </Col>

                <Col lg="4" md="6" sm="12" className="include_imgpartts d-flex">
                  <div className="include_img right w-100 d-flex" data-aos="fade-left" style={{ marginRight: 0 }}>
                    <img src={Section2Image} alt="The Solution - AMLyticsAI Platform" className="img-fluid w-100" style={{ borderRadius: '20px', objectFit: 'cover', height: '100%', minHeight: '380px' }} />
                  </div>
                </Col>
              </Row>
            </Container>
          </div>

          {/* Section 3: Our Approach */}
          <div className="include_service_innr" style={{ marginTop: '30px' }}>
            <Container>
              <Row>
                <Col lg="12">
                  <div className="include_text" data-aos="fade-up">
                    <h2 style={{ fontSize: '26px', fontWeight: 600, marginBottom: '20px' }}>Our Approach</h2>
                    <p style={{ fontSize: '16px', lineHeight: '26px' }}>
                      This is where Smart Resources QLD's core capabilities came together on a single project:
                    </p>
                    <div className="row mt-4">
                      <div className="col-md-6 mb-4">
                        <div style={{ backgroundColor: '#fff', padding: '25px', borderRadius: '15px', boxShadow: '0 5px 15px rgba(0,0,0,0.05)', height: '100%' }}>
                          <h3 style={{ fontSize: '18px', fontWeight: 600 }}>Data science</h3>
                          <p style={{ fontSize: '15px', color: '#555', marginTop: '10px' }}>
                            Designing the <NavLink to="/operations/data-science-services-australia" style={{ color: '#0d6efd', textDecoration: 'underline' }}>risk-scoring logic and alert models</NavLink> behind transaction monitoring and screening
                          </p>
                        </div>
                      </div>
                      <div className="col-md-6 mb-4">
                        <div style={{ backgroundColor: '#fff', padding: '25px', borderRadius: '15px', boxShadow: '0 5px 15px rgba(0,0,0,0.05)', height: '100%' }}>
                          <h3 style={{ fontSize: '18px', fontWeight: 600 }}>Software development</h3>
                          <p style={{ fontSize: '15px', color: '#555', marginTop: '10px' }}>
                            Building a secure, scalable platform (AWS-hosted, AES-256 encrypted, agency-isolated tenancy) from the ground up
                          </p>
                        </div>
                      </div>
                      <div className="col-md-6 mb-4">
                        <div style={{ backgroundColor: '#fff', padding: '25px', borderRadius: '15px', boxShadow: '0 5px 15px rgba(0,0,0,0.05)', height: '100%' }}>
                          <h3 style={{ fontSize: '18px', fontWeight: 600 }}>Regulatory research</h3>
                          <p style={{ fontSize: '15px', color: '#555', marginTop: '10px' }}>
                            Translating dense legislative obligations into a workflow a small compliance team can actually run day-to-day
                          </p>
                        </div>
                      </div>
                      <div className="col-md-6 mb-4">
                        <div style={{ backgroundColor: '#fff', padding: '25px', borderRadius: '15px', boxShadow: '0 5px 15px rgba(0,0,0,0.05)', height: '100%' }}>
                          <h3 style={{ fontSize: '18px', fontWeight: 600 }}>Product design</h3>
                          <p style={{ fontSize: '15px', color: '#555', marginTop: '10px' }}>
                            Making sure a sole practitioner or a small firm gets the same tools as a large reporting entity, without a steep learning curve
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="row mt-0">
                      <div className="col-md-12">
                        <p className="mt-3" style={{ fontSize: '16px', lineHeight: '26px' }}>
                          The result is a platform built specifically for how Australian reporting entities work, not a global product with local branding applied on top.
                        </p>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </Container>
          </div>

          {/* Section 4: Built for Businesses Australia-Wide (Image Left, Content Right) */}
          <div className="include_service_innr">
            <Container>
              <Row className="align-items-stretch">
                <Col lg="4" md="6" sm="12" className="d-flex mb-3 mb-lg-0">
                  <div className="include_img w-100 d-flex" data-aos="fade-right" style={{ marginLeft: 0 }}>
                    <img src={Section3Image} alt="Built for Businesses Australia-Wide" className="img-fluid w-100" style={{ borderRadius: '20px', objectFit: 'cover', height: '100%', minHeight: '320px' }} />
                  </div>
                </Col>

                <Col lg="8" md="6" sm="12" className="d-flex align-items-center">
                  <div className="include_text w-100" data-aos="fade-left">
                    <h2 className="mt-0">Built for Businesses Australia-Wide</h2>
                    <p style={{ fontSize: '16px', lineHeight: '26px' }}>
                      AMLyticsAI is a cloud-based platform, so reporting entities can onboard from anywhere in the country. We work with firms in Brisbane, Sydney, Melbourne, Perth, Adelaide, Canberra, and regional Australia alike. Wherever a business is based, if it provides a designated service under the Tranche 2 reforms, the same compliance obligations apply and the same platform can meet them.
                    </p>

                    <h2 className="mt-4">Why This Matters</h2>
                    <p style={{ fontSize: '16px', lineHeight: '26px' }}>
                      AMLyticsAI reflects how Smart Resources QLD approaches every engagement: understand the regulatory or operational problem first, then build (or advise on) a solution that's actually shaped around it, not a generic template. If your business needs custom software, data science capability, or a compliance solution built around Australian rules, this is the kind of work we do.
                    </p>
                  </div>
                </Col>
              </Row>
            </Container>
          </div>
        </section>

        <ServiceFaqSection
          title="Frequently Asked Questions"
          intro="Quick answers about AML/CTF compliance and AMLyticsAI."
          faqData={faqData}
          image={FaqImage}
          enableScrollableContainer={true}
          maxScrollHeight="520px"
          imageStyle={{ width: '100%', height: '520px', maxHeight: '520px', objectFit: 'cover' }}
        />
      </section>
    </div>
  );
};

export default AmlyticsAI;
